/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect, useRef } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ParticipantType, Company, Component } from '../../types';
import './CreatePdf.css';

// Import TinyMCE
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/skins/ui/oxide/skin.css';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

import tinymce from 'tinymce/tinymce';
import { registerMustachePlugin } from '../tinymce/mustacheTemplatePlugin';
registerMustachePlugin(tinymce);

const EditPdf: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const frontPageTemplateEditorRef = useRef<any>(null);
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    manufacturerName: '',
    manufacturerCode: '',
    price: '',
    expirationDate: '',
    companyId: '',
    filePath: '',
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [component, setComponent] = useState<Component | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (id) {
      fetchComponent(parseInt(id));
    }
  }, [id]);

  useEffect(() => {
    if (currentUser?.type === ParticipantType.ADMIN) {
      fetchCompanies();
    }
  }, [currentUser]);

  const fetchComponent = async (componentId: number) => {
    try {
      setIsFetching(true);
      setError(null);
      const response = await apiClient.getComponentById(componentId);
      if (response.success && response.component) {
        setComponent(response.component);
        const data = response.component;

        setFormData({
          name: data.name || '',
          manufacturerName: data.manufacturerName || '',
          manufacturerCode: data.manufacturerCode || '',
          price: data.price?.toString() || '',
          expirationDate: data.expirationDate ? data.expirationDate.split('T')[0] : '',
          companyId: data.company?.id.toString() || '',
          filePath: data.filePath || '',
        });

        // Set TinyMCE editor content
        if (frontPageTemplateEditorRef.current && data.frontPageTemplate) {
          frontPageTemplateEditorRef.current.setContent(data.frontPageTemplate);
        }
      } else {
        setError(response.error || 'Failed to load component');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading component');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await apiClient.getAllCompanies();
      if (response.success && response.companies) {
        setCompanies(response.companies);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.manufacturerName.trim()) {
      setError('Manufacturer name is required');
      return;
    }

    if (!formData.manufacturerCode.trim()) {
      setError('Manufacturer code is required');
      return;
    }

    if (!id) {
      setError('PDF ID is missing');
      return;
    }

    setIsLoading(true);

    try {
      // Get content from TinyMCE editor
      let frontPageTemplateContent = '';
      if (frontPageTemplateEditorRef.current) {
        // Wait for any pending uploads
        if (typeof frontPageTemplateEditorRef.current.uploadImages === 'function') {
          await frontPageTemplateEditorRef.current.uploadImages();
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        frontPageTemplateContent = frontPageTemplateEditorRef.current.getContent();
      }

      // Validate: must have at least one of file/filePath OR template (can have both)
      const willHaveFile = selectedFile !== null || (formData.filePath && formData.filePath.trim() !== '');
      const willHaveTemplate = frontPageTemplateContent && frontPageTemplateContent.trim() !== '';

      if (!willHaveFile && !willHaveTemplate) {
        setError('Component must have at least one of file or front page template');
        setIsLoading(false);
        return;
      }

      // Build FormData for multipart request
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('manufacturerName', formData.manufacturerName.trim());
      formDataToSend.append('manufacturerCode', formData.manufacturerCode.trim());

      if (formData.price) {
        formDataToSend.append('price', formData.price);
      }

      if (formData.expirationDate) {
        formDataToSend.append('expirationDate', formData.expirationDate);
      }

      if (formData.companyId) {
        formDataToSend.append('companyId', formData.companyId);
      }

      if (frontPageTemplateContent) {
        formDataToSend.append('frontPageTemplate', frontPageTemplateContent);
      }

      // Add file if selected
      if (selectedFile) {
        formDataToSend.append('file', selectedFile);
      }

      const response = await apiClient.updateComponent(parseInt(id), formDataToSend);

      if (response.success) {
        navigate(`/components/${id}`);
      } else {
        setError(response.error || 'Failed to update component');
      }
    } catch (err: any) {
      console.error('Error updating component:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while updating the component');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/components/${id}`);
  };

  if (isFetching) {
    return (
      <div className="create-pdf-container">
      <BackButton to="/components" />
        <div className="loading">Loading component details...</div>
      </div>
    );
  }

  if (!component) {
    return (
      <div className="create-pdf-container">
        <div className="error-message">
          {error || 'Component not found'}
        </div>
        <button onClick={() => navigate('/components')} className="back-button">
          Back to Components
        </button>
      </div>
    );
  }

  return (
    <div className="create-pdf-container">
      <div className="create-pdf-header">
        <h2>Edit Component</h2>
      </div>

      <form onSubmit={handleSubmit} className="create-pdf-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="name">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="Enter component name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="file">
            Replace Component File (Optional)
          </label>
          <input
            type="file"
            id="file"
            name="file"
            onChange={handleFileChange}
            disabled={isLoading}
            accept="application/pdf"
          />
          {selectedFile && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#059669' }}>
              New file selected: {selectedFile.name}
            </div>
          )}
          {!selectedFile && formData.filePath && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
              Current file: {formData.filePath.split('/').pop()}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="manufacturerName">
            Manufacturer Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="manufacturerName"
            name="manufacturerName"
            value={formData.manufacturerName}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="Enter manufacturer name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="manufacturerCode">
            Manufacturer Code <span className="required">*</span>
          </label>
          <input
            type="text"
            id="manufacturerCode"
            name="manufacturerCode"
            value={formData.manufacturerCode}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="Enter manufacturer code"
          />
        </div>

        <div className="form-group">
          <label htmlFor="price">
            Price
          </label>
          <input
            type="number"
            id="price"
            name="price"
            value={formData.price}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="Enter price"
            step="0.01"
            min="0"
          />
        </div>

        <div className="form-group">
          <label htmlFor="expirationDate">
            Expiration Date
          </label>
          <input
            type="date"
            id="expirationDate"
            name="expirationDate"
            value={formData.expirationDate}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        {currentUser?.type === ParticipantType.ADMIN ? (
          <div className="form-group">
            <label htmlFor="companyId">
              Company <span className="required">*</span>
            </label>
            <select
              id="companyId"
              name="companyId"
              value={formData.companyId}
              onChange={handleChange}
              disabled={isLoading}
              required
            >
              <option value="">Select a company</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        ) : component.company ? (
          <div className="form-group">
            <label htmlFor="companyDisplay">
              Company <span className="required">*</span>
            </label>
            <input
              type="text"
              id="companyDisplay"
              value={component.company.name}
              disabled
              style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
            />
          </div>
        ) : null}

        <div className="form-group">
          <label htmlFor="frontPageTemplate">Front Page Template</label>
          <Editor
            onInit={(evt, editor) => frontPageTemplateEditorRef.current = editor}
            licenseKey="gpl"
            init={{
              base_url: '/tinymce',
              suffix: '.min',
              height: 400,
              menubar: true,
              promotion: false,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'help', 'wordcount', 'mustachetemplate'
              ],
              toolbar: 'undo redo | blocks | inserttemplate | ' +
                'bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'image | removeformat | help',
              mustacheTemplateConfig: { collectionId: id ? parseInt(id) : null, mode: 'author' as const },
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              images_upload_handler: (blobInfo: any, progress: any) => new Promise<string>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.withCredentials = false;

                xhr.upload.onprogress = (e) => {
                  progress(e.loaded / e.total * 100);
                };

                xhr.onload = () => {
                  if (xhr.status === 403) {
                    reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
                    return;
                  }

                  if (xhr.status < 200 || xhr.status >= 300) {
                    reject('HTTP Error: ' + xhr.status);
                    return;
                  }

                  const json = JSON.parse(xhr.responseText);
                  if (!json || typeof json.location != 'string') {
                    reject('Invalid JSON: ' + xhr.responseText);
                    return;
                  }

                  resolve(json.location);
                };

                xhr.onerror = () => {
                  reject('Image upload failed due to a XHR Transport error. Code: ' + xhr.status);
                };

                const formData = new FormData();
                formData.append('file', blobInfo.blob(), blobInfo.filename());

                xhr.open('POST', '/api/tinymce/upload-image');
                const token = localStorage.getItem('jwt_token');
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.send(formData);
              }),
              automatic_uploads: true,
              paste_data_images: true,
              images_reuse_filename: false,
              file_picker_types: 'image',
              images_file_types: 'jpg,jpeg,png,gif,webp',
              relative_urls: true,
              remove_script_host: true,
              convert_urls: false
            }}
            disabled={isLoading}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="submit-button"
          >
            {isLoading ? 'Updating...' : 'Update Component'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPdf;
